import { apiSlice } from "../../../features/api/apiSlice";

export type ExecutiveOffice = {
  id: number;
  name: string;
  slug?: string;
  description?: string | null;
  role_id?: number | null;
  rank?: number | null;
};

export type ExecutiveMember = {
  id: string;
  user_id: number;
  membership_no?: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
  phone?: string;
};

export type Executive = {
  id: number;
  member_id: string;
  executive_set_id: number;
  executive_office_id: number;
  position?: string | null;
  member?: ExecutiveMember;
  office?: ExecutiveOffice;
  created_at?: string;
  updated_at?: string;
};

export type ExecutiveSet = {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  executives?: Executive[];
  necs?: any[];
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

function normalizeDetails(res: any): ExecutiveSet | null {
  const data = pick(res);
  // ✅ Your show() returns data: [ { ... } ]
  if (Array.isArray(data)) return data[0] ?? null;
  return data ?? null;
}

export const executiveSetApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getExecutiveSets: builder.query<ExecutiveSet[], void>({
      query: () => "/executive-sets",
      transformResponse: (res: any) => pick(res) || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({
                type: "ExecutiveSets" as const,
                id: s.id,
              })),
              { type: "ExecutiveSets" as const, id: "LIST" },
            ]
          : [{ type: "ExecutiveSets" as const, id: "LIST" }],
    }),

    getExecutiveSetDetails: builder.query<ExecutiveSet, number>({
      query: (id) => `/executive-sets/${id}`,
      transformResponse: (res: any) => normalizeDetails(res) as ExecutiveSet,
      providesTags: (_res, _err, id) => [
        { type: "ExecutiveSets" as const, id },
      ],
    }),

    createExecutiveSet: builder.mutation<
      ExecutiveSet,
      { name: string; start_date: string; end_date: string }
    >({
      query: (body) => ({
        url: "/executive-sets",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ExecutiveSets" as const, id: "LIST" }],
    }),

    activateExecutiveSet: builder.mutation<ExecutiveSet, number>({
      query: (id) => ({
        url: `/executive-sets/${id}/activate`,
        method: "POST",
      }),
      transformResponse: (res: any) => pick(res)?.data ?? pick(res),
      // Flip is_active immediately in the cache so the UI updates without
      // waiting for the background refetch triggered by invalidatesTags.
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          executiveSetApi.util.updateQueryData(
            "getExecutiveSets",
            undefined,
            (draft) => {
              for (const set of draft) {
                set.is_active = set.id === id;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: "ExecutiveSets" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetExecutiveSetsQuery,
  useGetExecutiveSetDetailsQuery,
  useCreateExecutiveSetMutation,
  useActivateExecutiveSetMutation,
} = executiveSetApi;
